import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { isUploadedAvatar } from '@/lib/avatar';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle2, Mail, Phone, Linkedin, ShieldCheck, ArrowRight, ArrowLeft, Loader2, Sparkles, X,
  MessageCircle, ExternalLink, Bell, Building2, Wand2, Plus, LogOut,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { WhatsAppLogo, SmsLogo } from '@/components/brand/ChannelLogos';
import { PHONE_OPTS, isoForDial, dialForIso } from '@/lib/countryCodes';
import { useAuth } from '@/hooks/use-auth';
import { usePush } from '@/hooks/use-push';
import AiAboutField from '@/components/ai/AiAboutField';
import AvatarUploadField from '@/components/media/AvatarUploadField';
import LocationFields from '@/components/profile/LocationFields';
import LocationPicker from '@/components/venture/LocationPicker';
import { LINKS_META } from '@/components/venture/VentureTabs';
import { PublishToFeedDialog } from '@/components/community/PublishToFeedDialog';

const ROLES = ['Founder', 'Co-founder', 'Partner', 'Investor', 'Advisor'];
const STAGES = ['Idea', 'Validation', 'MVP', 'Launch', 'Revenue', 'Scale'];
const SOCIAL_KEYS = ['website', 'linkedin', 'instagram', 'facebook', 'twitter', 'youtube'];
const STEPS = [
  { key: 'verify', label: 'Verify account', sub: 'Email & phone' },
  { key: 'profile', label: 'Your profile', sub: 'Who you are' },
  { key: 'company', label: 'Your company', sub: 'What you build' },
  { key: 'notifications', label: 'Notifications', sub: 'Stay in the loop' },
];

const ONBOARDING_SKILLS = ['Engineering', 'React', 'Node.js', 'Python', 'GenAI', 'Mobile', 'Design', 'Figma', 'Product', 'Growth', 'Marketing', 'Content', 'Sales', 'Ops', 'Finance', 'Data', 'Fundraising', 'Hiring'];

const EMPTY_PROFILE = {
  first_name: '', last_name: '', avatar: '', headline: '', seniority: '', department: '',
  city: '', state: '', country: 'India', linkedin: '', about: '', bio_oneliner: '', skills: [],
};
const EMPTY_COMPANY = {
  company_name: '', company_tagline: '', company_sector: '', company_industry: '', company_stage: 'Idea',
  company_website: '', company_linkedin_url: '', company_employee_count: null, company_employee_range: '',
  company_hq_city: '', company_hq_state: '', company_hq_country: '', company_about: '', company_logo: '',
  company_links: { website: '', linkedin: '', instagram: '', facebook: '', twitter: '', youtube: '' },
};

/**
 * Redesigned 4-step onboarding wizard (two-column, motion-driven):
 *   1. Verify account (email auto-verified + mandatory phone OTP; child profiles may skip)
 *   2. Your profile (LinkedIn fetch via Lusha + AI-drafted About)
 *   3. Your company (founder prompt + company fetch/select + AI About)
 *   4. Notifications (Email/SMS default on, WhatsApp verify, push enable)
 * Completed steps are auto-skipped on open.
 */
export const OnboardingFlow = ({ capitalMode = false, onDone = null }) => {
  const { authedFetch, user, updateUser, logout, applySession } = useAuth();
  const navigate = useNavigate();
  const push = usePush();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('verify');
  const [dir, setDir] = useState(1);
  const [sectors, setSectors] = useState([]);
  const [busy, setBusy] = useState(false);
  const completedRef = useRef(false);
  const loadedRef = useRef(false);
  const [welcomeFeed, setWelcomeFeed] = useState(null); // { defaultText, media }
  // Capital "Step 0" mode — the wizard runs from the public /capital page:
  // no company step, a reference rail item for the application, and completion
  // hands off to the application form.
  const steps = capitalMode
    ? [...STEPS.filter((s) => s.key !== 'company'), { key: 'capital_app', label: 'Bower Capital application', sub: 'Starts right after', refOnly: true }]
    : STEPS;

  // Nudge (post-onboarding WhatsApp reminder)
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [phoneDialog, setPhoneDialog] = useState(false);
  const [waConnect, setWaConnect] = useState(null);
  const wpollRef = useRef(null);

  // Phone
  const [cc, setCc] = useState('+91');
  const [phone, setPhone] = useState('');
  const [channel, setChannel] = useState('sms'); // 'sms' | 'whatsapp'
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState('');

  // Email verification (phone-first accounts have no verified email yet)
  const [emailToVerify, setEmailToVerify] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailCode, setEmailCode] = useState('');

  // Capital anonymous account creation (Step 0 — happens inside the Verify step)
  const [acct, setAcct] = useState({ email: '', code: '', sent: false });
  const setA = (k) => (e) => setAcct((p) => ({ ...p, [k]: e.target.value }));

  // Resend cooldown timers — mirror the server's 30s per-recipient limit.
  const [otpCd, setOtpCd] = useState(0);
  const [acctCd, setAcctCd] = useState(0);
  useEffect(() => { if (otpCd <= 0) return undefined; const t = setTimeout(() => setOtpCd(otpCd - 1), 1000); return () => clearTimeout(t); }, [otpCd]);
  useEffect(() => { if (acctCd <= 0) return undefined; const t = setTimeout(() => setAcctCd(acctCd - 1), 1000); return () => clearTimeout(t); }, [acctCd]);

  // Profile
  const [profileUrl, setProfileUrl] = useState('');
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptGuidelines, setAcceptGuidelines] = useState(false);
  const [acceptModeration, setAcceptModeration] = useState(false);
  const allAccepted = acceptTerms && acceptGuidelines && acceptModeration;
  const setP = (k, v) => { setProfile((f) => ({ ...f, [k]: v })); setInvalid((iv) => (iv[k] ? { ...iv, [k]: false } : iv)); };
  const [invalid, setInvalid] = useState({});
  const [aboutPrompt, setAboutPrompt] = useState('');
  const [aboutBusy, setAboutBusy] = useState(false);

  // Mandatory profile fields — the wizard can't be completed without them.
  // (One-liner and About are optional; About is auto-drafted by AI.)
  const REQUIRED_PROFILE = [
    ['first_name', 'First name'], ['last_name', 'Last name'],
    ['avatar', 'Profile photo'], ['state', 'State'], ['city', 'City'],
    ['skills', 'Skills'],
  ];
  const missingProfile = (p) => REQUIRED_PROFILE.filter(([k]) => (
    k === 'skills' ? !(p.skills || []).length
      : k === 'avatar' ? !isUploadedAvatar(p.avatar)
      : !String(p[k] || '').trim()
  ));
  const flagMissing = (miss) => {
    const inv = {};
    miss.forEach(([k]) => { inv[k] = true; });
    setInvalid(inv);
    toast.error('Complete the required fields', { description: miss.map(([, l]) => l).join(', ') });
    setTimeout(() => {
      document.querySelector(`[data-req="${miss[0][0]}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  };

  // Company
  const [isFounder, setIsFounder] = useState(null);   // null | true | false
  const [role, setRole] = useState('Founder');
  const [company, setCompany] = useState(EMPTY_COMPANY);
  const [cInvalid, setCInvalid] = useState({});
  const setC = (k, v) => { setCompany((f) => ({ ...f, [k]: v })); setCInvalid((iv) => (iv[k] ? { ...iv, [k]: false } : iv)); };
  const [fetchedCompanies, setFetchedCompanies] = useState([]); // [{...company}]
  const [companyUrl, setCompanyUrl] = useState('');
  const [cAboutPrompt, setCAboutPrompt] = useState('');
  const [cAboutBusy, setCAboutBusy] = useState(false);

  // Mandatory company fields — logo, tagline and About stay optional.
  const REQUIRED_COMPANY = [
    ['company_name', 'Company name'], ['company_sector', 'Sector'], ['company_stage', 'Stage'],
    ['company_industry', 'Industry'], ['company_employee_range', 'Team size'],
    ['company_hq_country', 'Country'], ['company_hq_state', 'State'], ['company_hq_city', 'City'],
  ];
  const missingCompany = (c) => REQUIRED_COMPANY.filter(([k]) => !String(c[k] || '').trim());

  // Notifications
  const [prefs, setPrefs] = useState({ email_enabled: true, sms_enabled: true, push_enabled: true });
  const waAutoRef = useRef(false);

  const call = useCallback(async (path, body, method = 'POST') => {
    const res = await authedFetch(path, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'Request failed');
    return data;
  }, [authedFetch]);

  const goto = (next) => {
    const order = steps.map((s) => s.key);
    setDir(order.indexOf(next) >= order.indexOf(step) ? 1 : -1);
    setStep(next);
  };

  // ---- load status + prefill ----
  useEffect(() => {
    if (!user || user.persona === 'admin') return;
    let cancelled = false;
    call('/api/onboarding/status', null, 'GET').then((s) => {
      if (cancelled) return;
      setStatus(s);
      setCc((prev) => s.country_code || prev || '+91');
      setPhone((prev) => s.phone || prev || '');
      setProfileUrl(s.linkedin_url || '');
      setProfile({
        ...EMPTY_PROFILE,
        first_name: s.first_name || user.first_name || '',
        last_name: s.last_name || user.last_name || '',
        avatar: s.avatar || user.avatar || '',
        headline: s.headline || '', seniority: s.seniority || '', department: s.department || '',
        city: s.city || '', state: s.state || '', country: s.country || 'India',
        linkedin: s.linkedin_url || '', about: s.about || '',
        bio_oneliner: s.bio_oneliner || s.headline || '',
        skills: Array.isArray(s.skills) ? s.skills : [],
      });
      if (s.company && s.company.company_name) {
        setCompany({ ...EMPTY_COMPANY, ...s.company });
        setFetchedCompanies([{ ...EMPTY_COMPANY, ...s.company }]);
        setIsFounder(true);
        if (s.company_role) setRole(s.company_role);
      }
      if (s.notif_prefs) setPrefs({
        email_enabled: s.notif_prefs.email_enabled !== false,
        sms_enabled: s.notif_prefs.sms_enabled !== false,
        push_enabled: s.notif_prefs.push_enabled !== false,
      });
      // Set the initial step + open the wizard ONCE. Subsequent re-runs (e.g.
      // saveProfile → updateUser changes `user`) must NOT reset the step back
      // to the first page. We no longer auto-skip completed steps.
      if (!loadedRef.current) {
        loadedRef.current = true;
        setStep('verify');
        const GATE_PERSONAS = ['student-build', 'student-seed', 'user'];
        const blocked = s.gate && s.gate.can_access === false && !s.is_child
          && (!s.persona || GATE_PERSONAS.includes(s.persona));
        if (capitalMode && s.onboarding_completed && !blocked) { if (onDone) onDone(); }
        else if (!s.onboarding_completed || blocked || capitalMode) {
          // Quick-signup arrivals with a booking/chat intent (/app/mentors/{id}?book=1|tab=chat)
          // finish that first — the profile wizard waits for the next visit.
          const intentDeepLink = /^\/app\/mentors\/[^/]+$/.test(window.location.pathname)
            && /(?:^|[?&])(?:book=1|tab=chat)(?:&|$)/.test(window.location.search);
          if (!(intentDeepLink && !blocked && !capitalMode)) setOpen(true);
        }
      }
    }).catch(() => {});
    call('/api/venture/meta', null, 'GET').then((m) => !cancelled && setSectors(m.sectors || [])).catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: deps audited (P-D lint cleanup)
  }, [user, call]);

  useEffect(() => {
    const reopen = () => { goto('verify'); setOpen(true); };
    window.addEventListener('founderos:open-onboarding', reopen);
    return () => window.removeEventListener('founderos:open-onboarding', reopen);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: deps audited (P-D lint cleanup)
  }, [status]);

  // Capital mode, anonymous visitor — open straight into account creation.
  useEffect(() => {
    if (capitalMode && !user && !loadedRef.current) { setStep('verify'); setOpen(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capitalMode, user]);

  useEffect(() => () => { if (wpollRef.current) clearInterval(wpollRef.current); }, []);

  // ---- phone OTP ----
  const sendOtp = async () => {
    if (!phone.trim()) { toast.error('Enter your phone number'); return; }
    setBusy(true);
    try {
      const r = await call('/api/onboarding/phone/send-otp', { country_code: cc, phone, channel });
      setOtpSent(true);
      setOtpCd(Number(r.retry_after) || 30);
      toast.success(`Code sent on ${r.channel === 'sms' ? 'SMS' : 'WhatsApp'}`);
    } catch (e) { toast.error('Could not send code', { description: e.message }); }
    finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    if (code.trim().length < 4) { toast.error('Enter the code'); return; }
    setBusy(true);
    try {
      const r = await call('/api/onboarding/phone/verify-otp', { code });
      if (r.account_switched && r.token) {
        applySession(r);
        toast.success('Welcome back — signed you into your existing account');
        setOtpSent(false); setCode('');
        return;
      }
      toast.success('Phone verified');
      setStatus((s) => ({
        ...(s || {}), phone_verified: true, whatsapp_connected: true,
        phone: r.phone || phone, country_code: r.country_code || cc,
      }));
      if (updateUser) updateUser({ ...user, phone_verified: true, gate: { ...(user?.gate || {}), phone_verified: true } });
      setOtpSent(false); setCode('');
    } catch (e) { toast.error('Verification failed', { description: e.message }); }
    finally { setBusy(false); }
  };

  // ---- email verification (for phone-first accounts) ----
  const sendEmailCode = async () => {
    const e = emailToVerify.trim().toLowerCase();
    if (!e.includes('@')) { toast.error('Enter a valid email address'); return; }
    setBusy(true);
    try {
      await call('/api/auth/email/send-code', { email: e });
      setEmailOtpSent(true);
      toast.success('Verification code sent', { description: `to ${e}` });
    } catch (err) { toast.error('Could not send the code', { description: err.message }); }
    finally { setBusy(false); }
  };
  const verifyEmailCode = async () => {
    if (emailCode.trim().length < 4) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      const r = await call('/api/auth/email/verify-code', { code: emailCode.trim() });
      setEmailOtpSent(false); setEmailCode('');
      if (r.account_switched && r.token) {
        applySession(r);
        toast.success('Welcome back — signed you into your existing account');
        return;
      }
      toast.success('Email verified');
      setStatus((s) => ({ ...(s || {}), email_verified: true, email: emailToVerify.trim().toLowerCase() }));
      if (updateUser && r.user) updateUser(r.user);
    } catch (err) { toast.error('Verification failed', { description: err.message }); }
    finally { setBusy(false); }
  };

  // ---- account creation (capital mode, anonymous — public endpoints) ----
  const pubPost = async (path, body) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Request failed');
    return data;
  };
  const sendAcctCode = async () => {
    const e = acct.email.trim().toLowerCase();
    if (!/.+@.+\..+/.test(e)) { toast.error('Enter a valid email address'); return; }
    setBusy(true);
    try { const r = await pubPost('/api/auth/email/login-send', { email: e }); setAcct((p) => ({ ...p, sent: true })); setAcctCd(Number(r.retry_after) || 30); toast.success('Code sent — check your inbox'); }
    catch (err) { toast.error('Could not send the code', { description: err.message }); }
    finally { setBusy(false); }
  };
  const verifyAcctCode = async () => {
    if (acct.code.trim().length < 6) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      const d = await pubPost('/api/auth/email/login-verify', {
        email: acct.email.trim().toLowerCase(), code: acct.code.trim(),
      });
      applySession(d); // user set → status loads → phone verification appears in this same step
      toast.success('Email verified');
    } catch (err) { toast.error('Verification failed', { description: err.message }); }
    finally { setBusy(false); }
  };

  // ---- LinkedIn fetch ----
  const fetchLinkedIn = async () => {
    if (!profileUrl.trim()) { toast.error('Paste your LinkedIn profile URL'); return; }
    setBusy(true);
    try {
      const r = await call('/api/onboarding/linkedin/import', { profile_url: profileUrl });
      const d = r.draft || {};
      const np = {
        ...profile,
        first_name: d.first_name || profile.first_name, last_name: d.last_name || profile.last_name,
        headline: d.headline || profile.headline, seniority: d.seniority || profile.seniority, department: d.department || profile.department,
        bio_oneliner: profile.bio_oneliner || d.headline || '',
        city: d.city || profile.city, state: d.state || profile.state, country: d.country || profile.country,
        avatar: d.avatar || profile.avatar,
        linkedin: d.linkedin || profileUrl,
      };
      setProfile(np);
      let nc = company;
      if (d.company_name) {
        nc = {
          ...EMPTY_COMPANY, company_name: d.company_name, company_industry: d.company_industry || '',
          company_website: d.company_website || '', company_linkedin_url: d.company_linkedin || '',
          company_employee_count: d.company_employee_count ?? null, company_employee_range: d.company_employee_range || '',
          company_hq_city: d.company_hq_city || '', company_hq_state: d.company_hq_state || '', company_hq_country: d.company_hq_country || '',
        };
        setFetchedCompanies([nc]);
        setCompany(nc);
      }
      if (r.ok) {
        toast.success('Fetched from LinkedIn', { description: 'Drafting your About with AI…' });
        // Auto-draft the About from the freshly-imported facts.
        genAboutUser({ profile: np, company: nc });
      } else toast.message('Nothing found', { description: 'Fill in your details manually.' });
    } catch (e) { toast.error('Fetch failed', { description: e.message }); }
    finally { setBusy(false); }
  };

  const fetchCompanyUrl = async () => {
    if (!companyUrl.trim()) { toast.error('Paste a company website, LinkedIn URL or name'); return; }
    setBusy(true);
    try {
      const r = await call('/api/onboarding/company/fetch', { url: companyUrl, name: companyUrl });
      if (r.ok && r.company?.company_name) {
        const rc = r.company;
        const c = {
          ...EMPTY_COMPANY, ...rc,
          company_logo: rc.company_logo || '',
          company_about: rc.company_description || '',
          company_links: {
            ...EMPTY_COMPANY.company_links,
            website: rc.company_website || '',
            linkedin: rc.company_linkedin || '',
          },
        };
        setFetchedCompanies((prev) => [...prev.filter((p) => p.company_name !== c.company_name), c]);
        setCompany(c);
        setCompanyUrl('');
        const drafted = !!rc.company_description;
        toast.success(`Found ${c.company_name}`, {
          description: drafted ? 'Auto-filled logo, About, location and socials.' : 'Auto-filled details — drafting About with AI…',
        });
        if (!drafted) genAboutCompany({ company: c });
      } else toast.message('No match', { description: 'Enter the company details manually.' });
    } catch (e) { toast.error('Lookup failed', { description: e.message }); }
    finally { setBusy(false); }
  };

  // ---- AI About (auto-drafts on fetch; `override` lets callers pass fresh data
  // before React state has flushed) ----
  const genAboutUser = async (override) => {
    const p = override?.profile || profile;
    const c = override?.company || company;
    setAboutBusy(true);
    try {
      const r = await call('/api/onboarding/ai/about-user', {
        first_name: p.first_name, last_name: p.last_name, headline: p.headline,
        seniority: p.seniority, department: p.department,
        company_name: c.company_name, company_industry: c.company_industry,
        city: p.city, country: p.country,
        prompt: aboutPrompt, current: aboutPrompt ? p.about : '',
      });
      if (r.about) { setP('about', r.about); setAboutPrompt(''); }
    } catch (e) { toast.error('Could not draft', { description: e.message }); }
    finally { setAboutBusy(false); }
  };
  const genAboutCompany = async (override) => {
    const c = override?.company || company;
    setCAboutBusy(true);
    try {
      const r = await call('/api/onboarding/ai/about-company', {
        company_name: c.company_name, tagline: c.company_tagline,
        sector: c.company_sector, industry: c.company_industry, website: c.company_website,
        employee_range: c.company_employee_range, hq_city: c.company_hq_city, hq_country: c.company_hq_country,
        prompt: cAboutPrompt, current: cAboutPrompt ? c.company_about : '',
      });
      if (r.about) { setC('company_about', r.about); setCAboutPrompt(''); }
    } catch (e) { toast.error('Could not draft', { description: e.message }); }
    finally { setCAboutBusy(false); }
  };

  // ---- Auto-draft About (no button press needed) ----
  // Fires ONCE when all mandatory fields are in and the About is still empty —
  // never overwrites text the user typed. Regenerate stays available for re-runs.
  const autoAboutUserRef = useRef(false);
  useEffect(() => {
    if (!open || step !== 'profile' || aboutBusy || autoAboutUserRef.current) return;
    if ((profile.about || '').trim()) return;
    if (missingProfile(profile).length) return;
    const t = setTimeout(() => {
      if (autoAboutUserRef.current || (profile.about || '').trim()) return;
      autoAboutUserRef.current = true;
      genAboutUser();
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, profile, aboutBusy]);

  const autoAboutCompanyRef = useRef(false);
  useEffect(() => {
    if (!open || step !== 'company' || !isFounder || cAboutBusy || autoAboutCompanyRef.current) return;
    if ((company.company_about || '').trim()) return;
    if (missingCompany(company).length) return;
    const t = setTimeout(() => {
      if (autoAboutCompanyRef.current || (company.company_about || '').trim()) return;
      autoAboutCompanyRef.current = true;
      genAboutCompany();
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, isFounder, company, cAboutBusy]);

  // ---- step saves ----
  const saveProfile = async () => {
    const miss = missingProfile(profile);
    if (miss.length) { flagMissing(miss); return; }
    setInvalid({});
    if (!allAccepted) { toast.error('Please accept Terms, Community Guidelines and the safety notice'); return; }
    setBusy(true);
    try {
      await call('/api/onboarding/profile', { ...profile, linkedin: profile.linkedin || profileUrl });
      // Record the community terms acceptance server-side so we don't ask again.
      try { await call('/api/safety/guidelines/accept', {}); } catch (_) { /* non-blocking */ }
      if (updateUser) updateUser({ ...user, first_name: profile.first_name, last_name: profile.last_name, name: `${profile.first_name} ${profile.last_name}`.trim(), avatar: profile.avatar || user.avatar });
      setStatus((s) => ({ ...(s || {}), first_name: profile.first_name }));
      goto(capitalMode ? 'notifications' : 'company');
    } catch (e) { toast.error('Could not save', { description: e.message }); }
    finally { setBusy(false); }
  };
  const saveCompany = async () => {
    if (isFounder) {
      // Mandatory-field parity with the Create Venture form so the wizard
      // cannot let a founder ship a half-filled profile.
      const miss = missingCompany(company);
      if (miss.length) {
        const inv = {};
        miss.forEach(([k]) => { inv[k] = true; });
        setCInvalid(inv);
        toast.error('Complete the required fields', { description: miss.map(([, l]) => l).join(', ') });
        setTimeout(() => {
          document.querySelector(`[data-req="${miss[0][0]}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 60);
        return;
      }
      setCInvalid({});
    }
    setBusy(true);
    try {
      if (isFounder && company.company_name.trim()) {
        const cleanLinks = Object.fromEntries(
          Object.entries(company.company_links || {}).filter(([, v]) => (v || '').trim()),
        );
        await call('/api/onboarding/company', {
          ...company, founder_role: role,
          company_website: cleanLinks.website || company.company_website || '',
          company_linkedin_url: cleanLinks.linkedin || company.company_linkedin_url || '',
          company_links: cleanLinks,
        });
        setStatus((s) => ({ ...(s || {}), has_venture: true, company_role: role }));
      }
      goto('notifications');
    } catch (e) { toast.error('Could not save', { description: e.message }); }
    finally { setBusy(false); }
  };

  // ---- WhatsApp ----
  // WhatsApp is auto-connected the moment the user's phone is verified (OTP). The
  // old send-a-code opt-in flow has been removed.

  const enablePush = async () => {
    if (!push.configured) { toast.message('Push not configured for this workspace'); return; }
    try { await push.enable(); setPrefs((p) => ({ ...p, push_enabled: true })); toast.success('Push notifications enabled on this device'); }
    catch (e) { toast.error('Could not enable push', { description: e.message }); }
  };

  const finish = async () => {
    const miss = missingProfile(profile);
    if (miss.length) { goto('profile'); flagMissing(miss); return; }
    setBusy(true);
    try {
      await call('/api/notifications/preferences', prefs, 'PUT');
      await call('/api/onboarding/complete', {});
      toast.success('You’re all set! Welcome to Founder OS 🎉');
      setStatus((s) => ({ ...(s || {}), onboarding_completed: true }));
      completedRef.current = true;
      window.dispatchEvent(new CustomEvent('founderos:profile-updated'));
      setOpen(false);
      if (capitalMode) { if (onDone) onDone(); return; }
      // Offer a welcome post to B-Cafe — pre-filled from what they just told us.
      const first = (profile.first_name || user?.first_name || user?.name || 'A new founder').trim();
      const compName = (company.company_name || '').trim();
      const compTag = (company.company_tagline || '').trim();
      const lines = [];
      if (isFounder && compName) {
        lines.push(`👋 Hi B-Cafe — I'm ${first}, building ${compName}${compTag ? ` — ${compTag}` : ''}.`);
      } else {
        lines.push(`👋 Hi B-Cafe — I'm ${first}, just joined Bower.`);
      }
      lines.push('');
      lines.push('Excited to learn, share and ship alongside you all.');
      setWelcomeFeed({
        defaultText: lines.join('\n'),
      });
    } catch (e) { toast.error('Could not finish', { description: e.message }); }
    finally { setBusy(false); }
  };

  const skipAll = async () => {
    if (capitalMode && !user) { setOpen(false); return; } // no account yet — just close
    if (gateBlocked) {
      toast.error('Verify your email and phone to continue', { description: 'These are required to access your dashboard.' });
      return;
    }
    try { await call('/api/onboarding/skip'); } catch { /* ignore */ }
    setStatus((s) => ({ ...(s || {}), onboarding_completed: true }));
    setOpen(false);
    if (capitalMode && onDone) onDone();
  };

  const signOut = () => {
    logout();
    navigate('/signin', { replace: true });
  };

  if ((!status && !(capitalMode && !user)) || status?.persona === 'admin') return null;

  const stepIndex = steps.findIndex((s) => s.key === step);
  const phoneOk = !!status?.phone_verified;
  const emailOk = !!status?.email_verified;
  const isChild = !!status?.is_child;
  // Server-authoritative gate: a non-child student/user MUST verify email +
  // phone (and set a name) before they can leave the wizard / reach the
  // dashboard. Other personas (alumni, mentor, investor…) see the wizard but
  // are never hard-locked — their accounts are often provisioned by staff.
  const GATE_PERSONAS = ['student-build', 'student-seed', 'user'];
  const gateEnforced = !status?.persona || GATE_PERSONAS.includes(status?.persona);
  const gateBlocked = !isChild && gateEnforced
    && ((status?.gate && status.gate.can_access === false) || !emailOk || !phoneOk);
  const verifyDone = isChild || (emailOk && phoneOk);
  const acctPhase = capitalMode && !user; // Step 0 account creation, pre-auth

  return (
    <>
      {/* Wizard */}
      <Dialog open={open} onOpenChange={(o) => { if (!o && !completedRef.current) skipAll(); }}>
        <DialogContent className="max-w-5xl w-full p-0 overflow-hidden gap-0 md:min-h-[640px] flex flex-col md:flex-row" data-testid="onboarding-wizard">
          <DialogTitle className="sr-only">Set up your Founder OS account</DialogTitle>
          <DialogDescription className="sr-only">Verify your account, build your profile and company, and choose notifications.</DialogDescription>

          {/* Left rail — vertical stepper */}
          <aside className="md:w-[280px] shrink-0 border-b md:border-b-0 md:border-r border-border bg-secondary/30 px-6 py-6 md:py-10 md:px-8">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-grotesk uppercase tracking-[0.18em] text-muted-foreground">
              <Sparkles className="h-3 w-3" /> Founder OS
            </span>
            <h2 className="hidden md:block font-display text-2xl mt-4 leading-tight">{capitalMode ? 'Step 0 — set up your profile.' : 'Let’s get you set up.'}</h2>
            <ol className="flex md:flex-col gap-1.5 md:gap-2 mt-4 md:mt-8 overflow-x-auto">
              {steps.map((s, i) => {
                const active = s.key === step;
                const done = !s.refOnly && i < stepIndex;
                return (
                  <li key={s.key} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors shrink-0 ${active ? 'bg-card border border-border shadow-sm' : ''} ${s.refOnly ? 'opacity-80' : ''}`} data-testid={`stepper-${s.key}`}>
                    <span className={`h-7 w-7 rounded-full grid place-items-center text-xs font-semibold shrink-0 transition-colors ${done ? 'bg-success text-success-foreground' : active ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
                      {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                    </span>
                    <div className="hidden md:block min-w-0">
                      <p className={`text-sm leading-tight ${active ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{s.label}</p>
                      <p className="text-[11px] text-muted-foreground">{s.sub}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
            <div className="mt-6 md:mt-10 md:pt-6 md:border-t md:border-border">
              {acctPhase ? (
                <p className="text-[12px] text-muted-foreground">Already on Bower?{' '}
                  <Link to="/signin" className="text-primary font-medium hover:underline" data-testid="step0-signin-link">Sign in</Link> — you’ll start at step 1.</p>
              ) : (
                <button type="button" onClick={signOut} data-testid="onboarding-switch-account"
                  className="flex items-center gap-2 text-[12px] text-muted-foreground hover:text-foreground transition-colors">
                  <LogOut className="h-3.5 w-3.5" /> Sign in with a different account
                </button>
              )}
            </div>
          </aside>

          {/* Right — active step */}
          <div className="flex-1 min-w-0 px-6 py-7 md:px-12 md:py-10 overflow-y-auto max-h-[80vh] md:max-h-[640px]">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={step}
                custom={dir}
                initial={{ opacity: 0, x: dir * 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: dir * -16 }}
                transition={{ duration: 0.22 }}
              >
                {/* STEP 1 — VERIFY */}
                {step === 'verify' && (
                  <div className="space-y-7" data-testid="onboarding-step-verify">
                    <Header icon={ShieldCheck}
                      title={acctPhase ? 'Create your Bower account' : 'Verify your account'}
                      desc={acctPhase ? 'Verify your email and phone to start your application — you’ll complete your profile right after.' : 'A verified email and phone keep your workspace, mentors and copilot secure.'} />
                    {acctPhase ? (
                      <div className="space-y-4" data-testid="step0-account-block">
                        <div><Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email *</Label>
                          <Input className="mt-2" type="email" value={acct.email} onChange={setA('email')} placeholder="you@startup.com" disabled={acct.sent} data-testid="step0-email" /></div>
                        {!acct.sent ? (
                          <Button onClick={sendAcctCode} disabled={busy} className="w-full rounded-full gap-2" data-testid="step0-send-email">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send verification code
                          </Button>
                        ) : (
                          <div className="space-y-3">
                            <Input value={acct.code} onChange={(e) => setAcct((p) => ({ ...p, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                              placeholder="••••••" inputMode="numeric" className="tracking-[0.4em] text-center text-lg" data-testid="step0-email-code" />
                            <div className="flex gap-2">
                              <Button variant="outline" onClick={sendAcctCode} disabled={busy || acctCd > 0} className="rounded-full" data-testid="step0-resend-email">
                                {acctCd > 0 ? `Resend (00:${String(acctCd).padStart(2, '0')})` : 'Resend'}
                              </Button>
                              <Button onClick={verifyAcctCode} disabled={busy || acct.code.trim().length < 6} className="flex-1 rounded-full gap-2" data-testid="step0-verify-email">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Verify email
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : emailOk ? (
                      <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3" data-testid="email-verified-chip">
                        <Mail className="h-5 w-5 text-muted-foreground shrink-0" />
                        <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{status.email}</p><p className="text-xs text-success flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Email verified</p></div>
                      </div>
                    ) : (
                      <div data-testid="email-verification-block">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email address {isChild ? '(optional)' : '*'}</Label>
                        <div className="mt-2 space-y-3">
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Input value={emailToVerify} onChange={(e) => setEmailToVerify(e.target.value)} placeholder="you@company.com" type="email" className="flex-1 bg-background" disabled={emailOtpSent} data-testid="onboarding-email-input" />
                            {!emailOtpSent && (
                              <Button onClick={sendEmailCode} disabled={busy} className="rounded-full gap-2 shrink-0" data-testid="onboarding-send-email-code">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send code
                              </Button>
                            )}
                          </div>
                          {emailOtpSent && (
                            <div className="space-y-3">
                              <Input value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••••" inputMode="numeric" className="tracking-[0.4em] text-center text-lg" data-testid="onboarding-email-code" />
                              <div className="flex gap-2">
                                <Button variant="outline" onClick={sendEmailCode} disabled={busy} className="rounded-full" data-testid="onboarding-resend-email-code">Resend</Button>
                                <Button onClick={verifyEmailCode} disabled={busy} className="flex-1 rounded-full gap-2" data-testid="onboarding-verify-email-code">
                                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Verify email
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div data-testid="phone-verification-block">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phone number {isChild ? '(optional for child accounts)' : '*'}</Label>
                      {phoneOk ? (
                        <div className="rounded-xl border border-success/30 bg-success/10 p-4 flex items-center gap-3 mt-2" data-testid="onboarding-phone-verified">
                          <Phone className="h-5 w-5 text-success shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium" data-testid="onboarding-phone-display">{(status?.country_code || cc)} {status?.phone || phone}</p>
                            <p className="text-xs text-success flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Phone verified</p>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2"><PhoneFields {...{ cc, setCc, phone, setPhone, channel, setChannel, otpSent, code, setCode, busy, verifyOtp }} cooldown={otpCd}
                          sendOtp={user ? sendOtp : () => toast.error('Verify your email first', { description: 'We text the code once your email is confirmed.' })} /></div>
                      )}
                    </div>
                    <Footer
                      left={isChild && !verifyDone ? <Button variant="ghost" onClick={() => goto('profile')} data-testid="onboarding-verify-skip">Skip for now</Button> : <span />}
                      right={
                        <Button onClick={() => (capitalMode && status?.onboarding_completed ? (onDone && onDone()) : goto('profile'))} disabled={!verifyDone} className="rounded-full gap-2" data-testid="onboarding-verify-continue">
                          Continue <ArrowRight className="h-4 w-4" />
                        </Button>
                      }
                    />
                    {!verifyDone && <p className="text-[11px] text-muted-foreground -mt-3">Verify your email and phone to continue.</p>}
                  </div>
                )}

                {/* STEP 2 — PROFILE */}
                {step === 'profile' && (
                  <div className="space-y-6" data-testid="onboarding-step-profile">
                    <Header icon={Linkedin} title="Your profile" desc="Pull your details from LinkedIn or fill them in. You can edit everything." />
                    <div className="rounded-xl border-2 border-[#0A66C2]/30 bg-[#0A66C2]/5 p-4">
                      <Label className="flex items-center gap-1.5 text-sm font-semibold"><Linkedin className="h-4 w-4 text-[#0A66C2]" /> Import from LinkedIn</Label>
                      <p className="text-xs text-muted-foreground mt-1 mb-3">Paste your LinkedIn <span className="font-medium text-foreground">profile URL</span> — we’ll auto-fill your name, headline, location and company.</p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input value={profileUrl} onChange={(e) => setProfileUrl(e.target.value)} placeholder="https://www.linkedin.com/in/your-handle" className="flex-1 bg-background" data-testid="onboarding-linkedin-profile" />
                        <Button onClick={fetchLinkedIn} disabled={busy} className="gap-2 shrink-0 rounded-full" data-testid="linkedin-fetch-btn">
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Fetch
                        </Button>
                      </div>
                    </div>
                    <div data-req="avatar" className={invalid.avatar ? 'rounded-xl ring-2 ring-destructive/70 p-2 -m-2' : ''}>
                      <AvatarUploadField
                        value={profile.avatar} onChange={(v) => setP('avatar', v)} folder="avatars"
                        label="Profile photo *" shape="circle" fallback={(profile.first_name || 'U').charAt(0).toUpperCase()}
                        testid="onboarding-avatar"
                      />
                      {invalid.avatar && <p className="text-xs text-destructive mt-1.5">Add a profile photo to continue.</p>}
                    </div>
                    <div className="grid grid-cols-2 gap-3" data-testid="profile-form-grid">
                      <Field label="First name *" v={profile.first_name} on={(v) => setP('first_name', v)} tid="onboarding-first-name" invalid={invalid.first_name} req="first_name" err="First name is required." />
                      <Field label="Last name *" v={profile.last_name} on={(v) => setP('last_name', v)} tid="onboarding-last-name" invalid={invalid.last_name} req="last_name" err="Last name is required." />
                    </div>
                    <div data-req="state" className={(invalid.state || invalid.city) ? 'rounded-xl ring-2 ring-destructive/70 p-2 -m-2' : ''}>
                      <LocationFields
                        value={{ country: profile.country, state: profile.state, city: profile.city }}
                        onChange={(loc) => { setProfile((f) => ({ ...f, ...loc })); setInvalid((iv) => ({ ...iv, state: false, city: false })); }}
                        testidPrefix="onboarding"
                      />
                      {(invalid.state || invalid.city) && <p className="text-xs text-destructive mt-1.5">State and city are required.</p>}
                    </div>
                    <div data-req="skills" className={invalid.skills ? 'rounded-xl ring-2 ring-destructive/70 p-2 -m-2' : ''} data-testid="onboarding-skills">
                      <Label className="text-sm font-semibold">Your skills *</Label>
                      <p className="text-xs text-muted-foreground mt-0.5 mb-2">Tap what you're strong at — this powers Cofounder Match and richer intros.</p>
                      <div className="flex flex-wrap gap-1.5">
                        {ONBOARDING_SKILLS.map((s) => {
                          const active = (profile.skills || []).includes(s);
                          return (
                            <button key={s} type="button"
                              onClick={() => setP('skills', active ? profile.skills.filter((x) => x !== s) : [...(profile.skills || []), s])}
                              data-testid={`onboarding-skill-${s.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                              className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${active ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:text-foreground bg-card'}`}>
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div data-req="bio_oneliner">
                      <Label className="text-sm font-semibold">One-liner</Label>
                      <p className="text-xs text-muted-foreground mt-0.5 mb-2">Optional — a short public tagline shown next to your name, auto-picked from LinkedIn when you fetch. ({(profile.bio_oneliner || '').length}/100)</p>
                      <Input
                        value={profile.bio_oneliner}
                        maxLength={100}
                        onChange={(e) => setP('bio_oneliner', e.target.value)}
                        placeholder="Founder building AI review tools · Ex-Notion PM · IIT Bombay '24"
                        data-testid="onboarding-oneliner"
                      />
                    </div>
                    <div data-req="about">
                      <AiAboutField
                        label="About you" value={profile.about} onChange={(v) => setP('about', v)} busy={aboutBusy}
                        onGenerate={genAboutUser} prompt={aboutPrompt} setPrompt={setAboutPrompt}
                        taTid="profile-about-ai" genTid="profile-about-regenerate" promptTid="profile-about-prompt" applyTid="profile-about-apply"
                      />
                      <p className="text-[11px] text-muted-foreground mt-1.5">Optional — AI drafts this automatically once your details above are filled in.</p>
                    </div>

                    {/* Community & AI acknowledgement — Apple 1.2 / 5.1.1 gate (one-time, inline). */}
                    <div className="rounded-xl border border-border p-4 bg-secondary/40 space-y-3" data-testid="onboarding-acknowledgements">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Before you continue</div>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox checked={acceptTerms} onCheckedChange={setAcceptTerms} data-testid="onboarding-terms-1" />
                        <span className="text-sm leading-snug">I agree to the <a href="https://bowerschool.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a>.</span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox checked={acceptGuidelines} onCheckedChange={setAcceptGuidelines} data-testid="onboarding-terms-2" />
                        <span className="text-sm leading-snug">I have read and agree to follow the <a href="/community-guidelines" target="_blank" rel="noreferrer" className="underline">Community Guidelines</a>.</span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox checked={acceptModeration} onCheckedChange={setAcceptModeration} data-testid="onboarding-terms-3" />
                        <span className="text-sm leading-snug">I understand that abusive or objectionable content may result in suspension of my account.</span>
                      </label>
                    </div>

                    <Footer
                      left={<Button variant="ghost" onClick={() => goto('verify')} className="gap-1.5" data-testid="onboarding-profile-back"><ArrowLeft className="h-4 w-4" /> Back</Button>}
                      right={<Button onClick={saveProfile} disabled={busy || !allAccepted} className="rounded-full gap-2" data-testid="onboarding-profile-continue">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirm & continue <ArrowRight className="h-4 w-4" /></Button>}
                    />
                  </div>
                )}

                {/* STEP 3 — COMPANY */}
                {step === 'company' && (
                  <div className="space-y-6" data-testid="onboarding-step-company">
                    <Header icon={Building2} title="Your company" desc={company.company_name ? `We found ${company.company_name}. Are you part of the founding team?` : 'Tell us about the company you’re building.'} />
                    <div className="grid grid-cols-2 gap-3" data-testid="founder-prompt-radios">
                      <button type="button" onClick={() => setIsFounder(true)} className={`rounded-xl border p-4 text-left transition-all ${isFounder === true ? 'border-primary ring-1 ring-primary bg-primary/5' : 'border-border hover:border-primary/40'}`} data-testid="founder-prompt-yes">
                        <CheckCircle2 className={`h-5 w-5 mb-2 ${isFounder === true ? 'text-primary' : 'text-muted-foreground'}`} />
                        <p className="font-medium text-sm">Yes, I’m a founder/partner</p><p className="text-xs text-muted-foreground mt-0.5">Create my company profile</p>
                      </button>
                      <button type="button" onClick={() => setIsFounder(false)} className={`rounded-xl border p-4 text-left transition-all ${isFounder === false ? 'border-primary ring-1 ring-primary bg-primary/5' : 'border-border hover:border-primary/40'}`} data-testid="founder-prompt-no">
                        <X className={`h-5 w-5 mb-2 ${isFounder === false ? 'text-primary' : 'text-muted-foreground'}`} />
                        <p className="font-medium text-sm">Not right now</p><p className="text-xs text-muted-foreground mt-0.5">Skip the company step</p>
                      </button>
                    </div>

                    {isFounder && (
                      <div className="space-y-5">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Your role</Label>
                            <Select value={role} onValueChange={setRole}>
                              <SelectTrigger className="mt-1.5" data-testid="company-role-select"><SelectValue /></SelectTrigger>
                              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4" data-testid="company-search-block">
                          <Label className="flex items-center gap-1.5 text-sm font-semibold"><Building2 className="h-4 w-4 text-primary" /> Find your company</Label>
                          <p className="text-xs text-muted-foreground mt-1 mb-3">Paste your company’s <span className="font-medium text-foreground">website, LinkedIn page URL or name</span> and we’ll fetch its details.</p>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <Input value={companyUrl} onChange={(e) => setCompanyUrl(e.target.value)} placeholder="e.g. stripe.com  ·  linkedin.com/company/stripe  ·  Stripe" className="flex-1 bg-background" data-testid="company-url-input" />
                            <Button onClick={fetchCompanyUrl} disabled={busy} className="gap-2 shrink-0 rounded-full" data-testid="company-url-fetch">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Fetch</Button>
                          </div>
                        </div>

                        {fetchedCompanies.length > 0 && (
                          <div data-testid="company-selector-cards">
                            <Label className="text-xs mb-2 block">Select your company</Label>
                            <div className="grid sm:grid-cols-2 gap-2">
                              {fetchedCompanies.map((c) => (
                                <button key={c.company_name} type="button" onClick={() => setCompany(c)} className={`rounded-xl border p-3 text-left transition-all ${company.company_name === c.company_name ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/40'}`} data-testid={`company-card-${(c.company_name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                  <p className="font-medium text-sm truncate">{c.company_name}</p>
                                  <p className="text-xs text-muted-foreground truncate">{c.company_industry || c.company_website}</p>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        <AvatarUploadField
                          value={company.company_logo} onChange={(v) => setC('company_logo', v)} folder="ventures"
                          label="Company logo" shape="square" fallback={(company.company_name || 'C').charAt(0).toUpperCase()}
                          testid="onboarding-company-logo"
                        />
                        <div className="grid grid-cols-2 gap-3" data-testid="company-details-form">
                          <Field label="Company name *" v={company.company_name} on={(v) => setC('company_name', v)} tid="onboarding-company-name" cls="col-span-2" invalid={cInvalid.company_name} req="company_name" err="Company name is required." />
                          <Field label="Tagline / One-liner" v={company.company_tagline} on={(v) => setC('company_tagline', v)} tid="onboarding-company-tagline" cls="col-span-2" />
                          <div data-req="company_sector">
                            <Label className="text-xs">Sector *</Label>
                            <Select value={company.company_sector || undefined} onValueChange={(v) => setC('company_sector', v)}>
                              <SelectTrigger className={`mt-1.5 ${cInvalid.company_sector ? 'border-destructive ring-1 ring-destructive' : ''}`} data-testid="onboarding-company-sector"><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>{sectors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                            {cInvalid.company_sector && <p className="text-xs text-destructive mt-1">Pick a sector.</p>}
                          </div>
                          <div data-req="company_stage">
                            <Label className="text-xs">Stage *</Label>
                            <Select value={company.company_stage || 'Idea'} onValueChange={(v) => setC('company_stage', v)}>
                              <SelectTrigger className={`mt-1.5 ${cInvalid.company_stage ? 'border-destructive ring-1 ring-destructive' : ''}`} data-testid="onboarding-company-stage"><SelectValue /></SelectTrigger>
                              <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <Field label="Industry *" v={company.company_industry} on={(v) => setC('company_industry', v)} tid="onboarding-company-industry" invalid={cInvalid.company_industry} req="company_industry" err="Industry is required." />
                          <Field label="Team size *" v={company.company_employee_range} on={(v) => setC('company_employee_range', v)} tid="onboarding-company-size" invalid={cInvalid.company_employee_range} req="company_employee_range" err="Team size is required." />
                        </div>

                        <div
                          data-req="company_hq_country"
                          className={(cInvalid.company_hq_country || cInvalid.company_hq_state || cInvalid.company_hq_city) ? 'rounded-xl ring-2 ring-destructive/70 p-2 -m-2' : ''}
                        >
                          <LocationPicker
                            value={{ hq_city: company.company_hq_city, hq_state: company.company_hq_state, hq_country: company.company_hq_country }}
                            onChange={(patch) => {
                              setCompany((p) => ({
                                ...p,
                                hq_city: undefined, hq_state: undefined, hq_country: undefined,
                                company_hq_city: patch.hq_city !== undefined ? patch.hq_city : p.company_hq_city,
                                company_hq_state: patch.hq_state !== undefined ? patch.hq_state : p.company_hq_state,
                                company_hq_country: patch.hq_country !== undefined ? patch.hq_country : p.company_hq_country,
                              }));
                              setCInvalid((iv) => ({ ...iv, company_hq_city: false, company_hq_state: false, company_hq_country: false }));
                            }}
                            required testidPrefix="onboarding-company"
                          />
                          {(cInvalid.company_hq_country || cInvalid.company_hq_state || cInvalid.company_hq_city) && (
                            <p className="text-xs text-destructive mt-1.5">Country, state and city are required.</p>
                          )}
                        </div>

                        <div>
                          <Label className="text-xs font-semibold">Website & socials</Label>
                          <div className="grid sm:grid-cols-2 gap-3 mt-2">
                            {SOCIAL_KEYS.map((k) => {
                              const m = LINKS_META.find((l) => l.key === k);
                              if (!m) return null;
                              return (
                                <div key={k}>
                                  <Label className="text-[11px] flex items-center gap-1.5 text-muted-foreground"><m.icon className="h-3 w-3" /> {m.label}</Label>
                                  <Input
                                    value={company.company_links?.[k] || ''}
                                    onChange={(e) => setCompany((p) => ({ ...p, company_links: { ...(p.company_links || {}), [k]: e.target.value } }))}
                                    placeholder={m.ph}
                                    className="mt-1"
                                    data-testid={`onboarding-company-link-${k}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <AiAboutField
                            label="About company" value={company.company_about} onChange={(v) => setC('company_about', v)} busy={cAboutBusy}
                            onGenerate={genAboutCompany} prompt={cAboutPrompt} setPrompt={setCAboutPrompt}
                            taTid="company-about-ai" genTid="company-about-regenerate" promptTid="company-about-prompt" applyTid="company-about-apply"
                          />
                          <p className="text-[11px] text-muted-foreground mt-1.5">Optional — AI drafts this automatically once the required fields above are filled in.</p>
                        </div>
                      </div>
                    )}

                    <Footer
                      left={<Button variant="ghost" onClick={() => goto('profile')} className="gap-1.5" data-testid="onboarding-company-back"><ArrowLeft className="h-4 w-4" /> Back</Button>}
                      right={
                        isFounder === false
                          ? <Button onClick={() => goto('notifications')} className="rounded-full gap-2" data-testid="onboarding-company-skip">Continue <ArrowRight className="h-4 w-4" /></Button>
                          : <Button onClick={saveCompany} disabled={busy || isFounder === null} className="rounded-full gap-2" data-testid="onboarding-company-continue">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirm & continue <ArrowRight className="h-4 w-4" /></Button>
                      }
                    />
                  </div>
                )}

                {/* STEP 4 — NOTIFICATIONS */}
                {step === 'notifications' && (
                  <div className="space-y-6" data-testid="onboarding-step-notifications">
                    <Header icon={Bell} title="Stay in the loop" desc="Choose how Bower reaches you about sessions, mentors and deadlines." />
                    <div className="rounded-xl border border-border divide-y divide-border">
                      <ToggleRow icon={Mail} title="Email updates" desc={status.email} checked={prefs.email_enabled} onChange={(v) => setPrefs((p) => ({ ...p, email_enabled: v }))} tid="notify-email" />
                      <ToggleRow icon={Phone} title="SMS alerts" desc="Important, time-sensitive alerts" checked={prefs.sms_enabled} onChange={(v) => setPrefs((p) => ({ ...p, sms_enabled: v }))} tid="notify-sms" />
                      <ActionRow icon={MessageCircle} title="WhatsApp" desc={status.whatsapp_connected ? 'Connected' : 'Auto-connects once your phone is verified'}
                        done={status.whatsapp_connected}
                        action={status.whatsapp_connected ? null : <span className="text-[10px] text-muted-foreground border border-border rounded-full px-2 py-0.5">Verify phone</span>} tid="notify-wa" />
                      <ActionRow icon={Bell} title="Push notifications" desc={push.deviceEnabled ? 'Enabled on this device' : push.configured ? 'Get instant alerts in your browser' : 'Not available in this workspace'}
                        done={push.deviceEnabled}
                        action={<Button size="sm" variant="outline" className="rounded-full" onClick={enablePush} disabled={push.deviceEnabled || !push.configured} data-testid="notify-push-enable">{push.deviceEnabled ? 'Enabled' : 'Enable'}</Button>} tid="notify-push" />
                    </div>

                    {status.whatsapp_connected && (
                      <div className="rounded-xl border border-success/30 bg-success/10 p-3 flex items-center gap-2 text-sm" data-testid="wa-connected"><CheckCircle2 className="h-4 w-4 text-success" /> WhatsApp connected</div>
                    )}
                    <Footer
                      left={<Button variant="ghost" onClick={() => goto(capitalMode ? 'profile' : 'company')} className="gap-1.5" data-testid="onboarding-notif-back"><ArrowLeft className="h-4 w-4" /> Back</Button>}
                      right={<Button onClick={finish} disabled={busy} className="rounded-full gap-2" data-testid="onboarding-finish">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Finish setup</Button>}
                    />
                    <div className="text-center"><Button variant="link" size="sm" className="text-muted-foreground" onClick={skipAll} data-testid="onboarding-skip-all">Skip for now</Button></div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </DialogContent>
      </Dialog>
      <PublishToFeedDialog
        open={!!welcomeFeed}
        onOpenChange={(v) => { if (!v) setWelcomeFeed(null); }}
        title="Say hi to the community?"
        description="Introduce yourself in B-Cafe. Fellow founders will love to meet you."
        defaultText={welcomeFeed?.defaultText || ''}
        tag="Update"
        testid="onboarding-welcome-feed"
      />
    </>
  );
};

// ---------- small building blocks ----------
const Header = ({ icon: Icon, title, desc }) => (
  <div>
    <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center mb-3"><Icon className="h-5 w-5" /></div>
    <h2 className="font-display text-2xl sm:text-3xl tracking-tight">{title}</h2>
    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{desc}</p>
  </div>
);

const Footer = ({ left, right }) => (
  <div className="flex items-center justify-between pt-2">{left}{right}</div>
);

const Field = ({ label, v, on, tid, cls = '', invalid = false, req, err }) => (
  <div className={cls} data-req={req}>
    <Label className="text-xs">{label}</Label>
    <Input value={v} onChange={(e) => on(e.target.value)} className={`mt-1.5 ${invalid ? 'border-destructive ring-1 ring-destructive' : ''}`} data-testid={tid} />
    {invalid && <p className="text-xs text-destructive mt-1">{err || 'This field is required.'}</p>}
  </div>
);

const ToggleRow = ({ icon: Icon, title, desc, checked, onChange, tid }) => (
  <div className="flex items-center gap-3 p-4">
    <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
    <div className="min-w-0 flex-1"><p className="text-sm font-medium">{title}</p><p className="text-xs text-muted-foreground truncate">{desc}</p></div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={tid} />
  </div>
);

const ActionRow = ({ icon: Icon, title, desc, action, done, tid }) => (
  <div className="flex items-center gap-3 p-4" data-testid={tid}>
    <Icon className={`h-5 w-5 shrink-0 ${done ? 'text-success' : 'text-muted-foreground'}`} />
    <div className="min-w-0 flex-1"><p className="text-sm font-medium flex items-center gap-1.5">{title}{done && <CheckCircle2 className="h-3.5 w-3.5 text-success" />}</p><p className="text-xs text-muted-foreground truncate">{desc}</p></div>
    {action}
  </div>
);

// Shared phone + OTP fields
// `busy` = a request is genuinely in flight (spinner + "Sending…"/"Verifying…").
// `disabled` = the block isn't actionable yet (e.g. email not verified) — buttons
// are inert but keep their normal label; never show a fake pending state.
export const PhoneFields = ({ cc, setCc, phone, setPhone, channel, setChannel, otpSent, code, setCode, busy, sendOtp, verifyOtp, cooldown = 0, disabled = false }) => (
  <div className="space-y-3">
    <div className="flex gap-2">
      <div className="w-[104px] shrink-0">
        <Combobox testid="onboarding-country-code" options={PHONE_OPTS} value={isoForDial(cc)}
          onChange={(v) => setCc(dialForIso(v))} placeholder="Code" searchPlaceholder="Country / code" triggerClassName="px-2.5" />
      </div>
      <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="98765 43210" className="flex-1" disabled={otpSent} data-testid="onboarding-phone-input" />
    </div>
    {!otpSent && (
      <div className="grid grid-cols-2 gap-2" data-testid="onboarding-channel-select">
        {[{ id: 'sms', label: 'SMS', Logo: SmsLogo }, { id: 'whatsapp', label: 'WhatsApp', Logo: WhatsAppLogo }].map(({ id, label, Logo }) => (
          <button key={id} type="button" onClick={() => setChannel(id)} data-testid={`onboarding-channel-${id}`}
            className={`flex items-center justify-center gap-2 h-10 rounded-full border text-sm transition-all ${channel === id ? 'border-primary ring-1 ring-primary bg-primary/5 font-medium' : 'border-border hover:border-primary/40'}`}>
            <Logo className="h-5 w-5" /> {label}
          </button>
        ))}
      </div>
    )}
    {!otpSent ? (
      <Button onClick={sendOtp} disabled={busy || disabled || !String(phone || '').trim()} className="w-full rounded-full gap-2" data-testid="onboarding-send-otp">
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : `Send ${channel === 'sms' ? 'SMS' : 'WhatsApp'} code`}
      </Button>
    ) : (
      <div className="space-y-3">
        <div className="flex items-center gap-2 rounded-full bg-success/10 border border-success/30 px-3 py-1.5 text-[12px] text-success w-fit" data-testid="onboarding-sent-chip">
          {channel === 'whatsapp' ? <WhatsAppLogo className="h-4 w-4" /> : <SmsLogo className="h-4 w-4" />}
          Code sent via {channel === 'whatsapp' ? 'WhatsApp' : 'SMS'} · on its way
        </div>
        <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••••" inputMode="numeric" className="tracking-[0.4em] text-center text-lg" data-testid="onboarding-otp-input" />
        <div className="flex gap-2">
          <Button variant="outline" onClick={sendOtp} disabled={busy || disabled || cooldown > 0} className="rounded-full" data-testid="onboarding-resend-otp">
            {cooldown > 0 ? `Resend OTP (00:${String(cooldown).padStart(2, '0')})` : 'Resend'}
          </Button>
          <Button onClick={verifyOtp} disabled={busy || disabled || String(code || '').length < 4} className="flex-1 rounded-full gap-2" data-testid="onboarding-verify-otp">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</> : 'Verify'}
          </Button>
        </div>
      </div>
    )}
  </div>
);

export default OnboardingFlow;
